import React, { useState } from 'react';
import {
    Button,
    TextField,
    Container,
    Typography,
    Box,
    Alert,
    IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from "../context/AuthContext";
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';

const ForgotPassword = () => {
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { resetPassword } = useAuth(); // Assuming you'll add this to your AuthContext
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!email) {
            setError('Please enter your email address');
            return;
        }

        try {
            setError('');
            setSuccessMessage('');
            setIsSubmitting(true);

            await resetPassword(email);
            setSuccessMessage('Password reset link sent! Check your email inbox and spam folder.');

            // Optional: Clear the email field after successful submission
            setEmail('');
        } catch (error) {
            if (error.code === 'auth/user-not-found') {
                setError('No account found with this email address');
            } else if (error.code === 'auth/invalid-email') {
                setError('Please enter a valid email address');
            } else {
                setError('Failed to send reset email. Please try again.');
            }
            console.error(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Container
            maxWidth={false}
            sx={{
                minHeight: '100vh',
                backgroundColor: '#0D47A1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 3
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    maxWidth: '480px',
                    backgroundColor: '#FFFFFF',
                    borderRadius: 2,
                    padding: 4,
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        position: 'relative',
                    }}
                >
                    {/* Back button */}
                    <IconButton
                        sx={{
                            position: 'absolute',
                            left: 0,
                            top: 0
                        }}
                        onClick={() => navigate('/login')}
                    >
                        <ArrowBackIcon />
                    </IconButton>

                    {/* Error Alert */}
                    {error && (
                        <Alert
                            severity="error"
                            sx={{
                                width: '100%',
                                mb: 2,
                                '& .MuiAlert-message': {
                                    width: '100%',
                                    textAlign: 'center'
                                }
                            }}
                        >
                            {error}
                        </Alert>
                    )}

                    {/* Success Alert */}
                    {successMessage && (
                        <Alert
                            severity="success"
                            sx={{
                                width: '100%',
                                mb: 2,
                                '& .MuiAlert-message': {
                                    width: '100%',
                                    textAlign: 'center'
                                }
                            }}
                        >
                            {successMessage}
                        </Alert>
                    )}

                    <Box
                        component="img"
                        src="/icon.png"
                        sx={{ width: 48, height: 48, mb: 3 }}
                    />
                    <Typography
                        variant="h4"
                        sx={{
                            mb: 2,
                            fontWeight: 600,
                        }}
                    >
                        Reset Password
                    </Typography>
                    <Typography
                        sx={{
                            color: 'text.secondary',
                            mb: 4,
                            textAlign: 'center'
                        }}
                    >
                        Enter your email address and we'll send you a link to reset your password.
                    </Typography>

                    <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                        <TextField
                            fullWidth
                            label="Email"
                            variant="outlined"
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                setError(''); // Clear error on input change
                            }}
                            sx={{ mb: 3 }}
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={isSubmitting}
                            sx={{
                                py: 1.5,
                                textTransform: 'none',
                                fontSize: '1rem'
                            }}
                        >
                            {isSubmitting ? 'Sending...' : 'Send Reset Link'}
                        </Button>
                    </form>

                    <Box sx={{ mt: 3, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            Remember your password?{' '}
                            <Typography
                                component="span"
                                sx={{
                                    color: 'primary.main',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        textDecoration: 'underline'
                                    }
                                }}
                                onClick={() => navigate('/login')}
                            >
                                Sign in
                            </Typography>
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Container>
    );
};

export default ForgotPassword;